import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Trophy, Award, Medal, Crown, Zap, Target, TrendingUp, Swords } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getGlobalLeaderboard,
  getMultiplayerLeaderboard,
  getSinglePlayerLeaderboard,
  getUserRank
} from '../services/leaderboard.service';
import { LeaderboardEntry } from '../types';

type LeaderboardTab = 'global' | 'multiplayer' | 'singleplayer';

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  max-width: 900px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
`;

const Tab = styled(motion.button)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.surface};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? '#fff' : theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 767px) {
    font-size: 0.9rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const LeaderboardContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const UserRankCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.primary}20;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const RankBadge = styled.div<{ $isTop: boolean }>`
  min-width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme, $isTop }) =>
    $isTop ? theme.colors.secondary : theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const UserStats = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LeaderboardRow = styled(motion.div)<{ $highlight: boolean }>`
  background: ${({ theme, $highlight }) =>
    $highlight ? theme.colors.primary + '10' : theme.colors.surface};
  border: 2px solid ${({ theme, $highlight }) =>
    $highlight ? theme.colors.primary : 'transparent'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const Position = styled.div<{ $isTop3: boolean }>`
  min-width: 40px;
  text-align: center;
  font-size: ${({ $isTop3 }) => $isTop3 ? '1.5rem' : '1.1rem'};
  font-weight: 700;
  color: ${({ theme, $isTop3 }) =>
    $isTop3 ? theme.colors.secondary : theme.colors.textSecondary};
`;

const Avatar = styled.div<{ $avatar: number }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  color: white;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const PlayerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerRank = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 600;
  margin-left: ${({ theme }) => theme.spacing.xs};
`;

const StatGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  text-align: right;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};
    align-items: flex-end;
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 80px;
`;

const StatValue = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.error};
  font-size: 1.1rem;
  background: ${({ theme }) => theme.colors.error}20;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const getAvatarIcon = (avatar: number) => {
  const avatars = [
    <Trophy />,     // 0
    <Award />,      // 1
    <Medal />,      // 2
    <Crown />,      // 3
    <Zap />,        // 4
    <Target />,     // 5
    <TrendingUp />, // 6
    <Swords />,     // 7
    <Trophy />,     // 8
    <Award />,      // 9
  ];
  return avatars[avatar] || <Trophy />;
};

const PositionIcon = styled.div<{ $position: number }>`
  color: ${({ $position, theme }) => {
    if ($position === 1) return '#FFD700'; // Gold
    if ($position === 2) return '#C0C0C0'; // Silver
    if ($position === 3) return '#CD7F32'; // Bronze
    return theme.colors.textSecondary;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const getPositionDisplay = (position: number) => {
  if (position === 1) return <PositionIcon $position={1}><Crown size={32} /></PositionIcon>;
  if (position === 2) return <PositionIcon $position={2}><Medal size={28} /></PositionIcon>;
  if (position === 3) return <PositionIcon $position={3}><Award size={28} /></PositionIcon>;
  return `${position}`;
};

const LeaderboardScreen: React.FC = () => {
  const { currentUser: user } = useAuth();
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('global');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, [activeTab]);

  useEffect(() => {
    if (user) {
      loadUserRank();
    }
  }, [user, activeTab]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);

    try {
      let data: LeaderboardEntry[];

      if (activeTab === 'global') {
        data = await getGlobalLeaderboard(100);
      } else if (activeTab === 'multiplayer') {
        data = await getMultiplayerLeaderboard(100);
      } else {
        data = await getSinglePlayerLeaderboard(100);
      }

      setLeaderboard(data);
    } catch (err: any) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserRank = async () => {
    if (!user) return;

    try {
      const rank = await getUserRank(user.uid, activeTab);
      setUserRank(rank);
    } catch (err) {
      console.error('Error loading user rank:', err);
    }
  };

  const getStatLabel = () => {
    if (activeTab === 'global') return 'XP';
    if (activeTab === 'multiplayer') return 'Wins';
    return 'Best Score';
  };

  const getStatValue = (entry: LeaderboardEntry) => {
    if (activeTab === 'global') return entry.xp.toLocaleString();
    if (activeTab === 'multiplayer') return entry.gamesWon.toLocaleString();
    return entry.totalScore.toLocaleString();
  };

  return (
    <Container>
      <Header>
        <Title>Leaderboard</Title>
        <Subtitle>Compete with players around the world</Subtitle>
      </Header>

      <TabContainer>
        <Tab
          $active={activeTab === 'global'}
          onClick={() => setActiveTab('global')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Trophy size={18} style={{ marginRight: '8px' }} /> Global
        </Tab>
        <Tab
          $active={activeTab === 'multiplayer'}
          onClick={() => setActiveTab('multiplayer')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Swords size={18} style={{ marginRight: '8px' }} /> Multiplayer
        </Tab>
        <Tab
          $active={activeTab === 'singleplayer'}
          onClick={() => setActiveTab('singleplayer')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Target size={18} style={{ marginRight: '8px' }} /> Single Player
        </Tab>
      </TabContainer>

      <LeaderboardContainer>
        {user && userRank > 0 && userRank <= 100 && (
          <UserRankCard
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <RankBadge $isTop={userRank <= 10}>#{userRank}</RankBadge>
            <UserInfo>
              <UserName>You ({user.displayName})</UserName>
              <UserStats>
                {activeTab === 'global' && `${user.xp.toLocaleString()} XP`}
                {activeTab === 'multiplayer' && `${user.multiplayerStats.wins} wins (${user.multiplayerStats.winRate}% win rate)`}
                {activeTab === 'singleplayer' && `Best: ${user.stats.bestScore}`}
              </UserStats>
            </UserInfo>
          </UserRankCard>
        )}

        {loading && <LoadingMessage>Loading leaderboard...</LoadingMessage>}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!loading && !error && leaderboard.length === 0 && (
          <LoadingMessage>No players found. Be the first!</LoadingMessage>
        )}

        {!loading && !error && leaderboard.length > 0 && (
          <LeaderboardList>
            {leaderboard.map((entry, index) => (
              <LeaderboardRow
                key={entry.uid}
                $highlight={user?.uid === entry.uid}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
              >
                <Position $isTop3={index < 3}>
                  {getPositionDisplay(index + 1)}
                </Position>
                <Avatar $avatar={entry.avatar}>
                  {getAvatarIcon(entry.avatar)}
                </Avatar>
                <PlayerInfo>
                  <PlayerName>
                    {entry.displayName}
                    <PlayerRank>{entry.rank}</PlayerRank>
                  </PlayerName>
                </PlayerInfo>
                <StatGroup>
                  <Stat>
                    <StatValue>{getStatValue(entry)}</StatValue>
                    <StatLabel>{getStatLabel()}</StatLabel>
                  </Stat>
                  {activeTab === 'multiplayer' && (
                    <Stat>
                      <StatValue>{entry.winRate}%</StatValue>
                      <StatLabel>Win Rate</StatLabel>
                    </Stat>
                  )}
                </StatGroup>
              </LeaderboardRow>
            ))}
          </LeaderboardList>
        )}
      </LeaderboardContainer>
    </Container>
  );
};

export default LeaderboardScreen;
